import {
  DependencyError,
  PrepareDocumentsItemInfo,
  sanitizeFilename
} from '@cumu/shared';
import { controller, target, targets } from '@github/catalyst';
import { allFormData, busySubmitButtons } from '../../forms/utilities';
import { displayInternalServerError } from '../../forms/validation';
import {
  PdfMergeInstructionImageItem,
  PdfMergeInstructionItem,
  PdfMergeInstructionPdfItem,
  processPdfInstruction
} from '../../pdf';
import { zipFiles } from '../../zip';
import { PdfViewerElement } from './pdf-viewer';

@controller('prepare-pdf-documents')
export class PreparePdfDocumentsElement extends HTMLElement {
  @targets declare viewerElements: (PdfViewerElement | HTMLDivElement)[];
  @target declare itemInfos: HTMLScriptElement;
  @targets declare editTemplateLinks: HTMLAnchorElement[];
  @target declare primaryForm: HTMLFormElement;
  @target declare reviewPanel: HTMLElement;
  @target declare confirmPanel: HTMLFormElement;
  @target declare sentPanel: HTMLElement;
  current = 0;
  hasChanges = false;
  isSubmitting = false;

  selectTemplate(event: Event) {
    const index = +(event.target as HTMLSelectElement).value;
    if (index === this.current) {
      return;
    }
    this.viewerElements[this.current].classList.add('v-hidden');
    this.editTemplateLinks[this.current].hidden = true;
    this.current = index;
    this.viewerElements[this.current].classList.remove('v-hidden');
    this.editTemplateLinks[this.current].hidden = false;
  }

  cancelSend() {
    this.reviewPanel.classList.remove('v-hidden');
    this.confirmPanel.hidden = true;
  }

  async submit(event: CustomEvent<FormData>) {
    event.preventDefault();
    if (this.isSubmitting) {
      return;
    }
    const submitType = event.detail.get('type') as
      'zip' | 'pdf' | 'send' | 'pdf-for-send' | 'confirm-send' | unknown;

    if (submitType === 'confirm-send') {
      this.reviewPanel.classList.add('v-hidden');
      this.confirmPanel.hidden = false;
      return;
    }

    try {
      this.isSubmitting = true;
      await Promise.resolve();

      busySubmitButtons(this.primaryForm, true);
      busySubmitButtons(this.confirmPanel, true);

      let filename = (
        this.primaryForm.elements.namedItem('filename') as HTMLInputElement
      ).value.trim();

      const itemInfos: PrepareDocumentsItemInfo[] = JSON.parse(
        this.itemInfos.textContent!
      );
      const items: PdfMergeInstructionItem[] = await Promise.all(
        this.viewerElements.map(async (el, i) => {
          const info = itemInfos[i];
          if (el instanceof PdfViewerElement) {
            if (info.type !== 'pdf') {
              throw new Error(`Expected PDF item, got ${info.type}`);
            }
            const data = await el.save({
              rasterizeEncrypted: submitType !== 'zip'
            });
            return {
              type: 'pdf',
              title: info.title,
              filename: info.filename,
              start_page: info.start_page,
              end_page: info.end_page,
              readonly: info.readonly,
              data,
              remove_fields:
                submitType === 'send' || submitType === 'pdf-for-send'
                  ? el.signatureFields.map(({ field }) => field.name)
                  : undefined,
              right_signature_placeholders:
                submitType === 'send' || submitType === 'pdf-for-send'
                  ? el.signatureFields.map(({ field, value }) => {
                      const page_index = field.page!;
                      const [x1, y1] = field.rect!;
                      const x = x1;
                      let y: number;
                      const size = 14;
                      if (value.startsWith('[s')) {
                        y = y1 + 4;
                        value = value.replace(']', '              ]');
                      } else {
                        y = y1 + 3;
                        value = value.replace(']', '              ]');
                      }
                      return {
                        value,
                        page_index,
                        x,
                        y,
                        size
                      };
                    })
                  : undefined
            } satisfies PdfMergeInstructionPdfItem;
          } else {
            if (info.type !== 'image') {
              throw new Error(`Expected image item, got ${info.type}`);
            }
            const response = await fetch(info.cors_url);
            if (!response.ok) {
              throw new DependencyError(response, await response.text());
            }
            const data = await response.arrayBuffer();
            return {
              type: 'image',
              filename: info.filename,
              data: new Uint8Array<ArrayBuffer>(data),
              group: info.group,
              content_type: info.content_type
            } satisfies PdfMergeInstructionImageItem;
          }
        })
      );

      if (submitType === 'zip') {
        if (!/\.zip$/i.test(filename)) {
          filename += '.zip';
        }
        const data = await zipFiles(
          items.map(({ filename, data }) => ({ filename, data })),
          'Created by ' +
            this.getAttribute('user_name')! +
            ' at ' +
            this.getAttribute('organization_name')!
        );
        const url = URL.createObjectURL(
          new Blob([data], { type: 'application/zip' })
        );
        download(url, sanitizeFilename(filename));
        return;
      }

      if (!/\.pdf$/i.test(filename)) {
        filename += '.pdf';
      }
      const data = await processPdfInstruction({
        type: 'merge',
        debug: submitType === 'pdf-for-send',
        resize_to_letter:
          submitType === 'send' || submitType === 'pdf-for-send',
        title: filename,
        author:
          this.getAttribute('user_name')! +
          ' at ' +
          this.getAttribute('organization_name')!,
        items
      });
      const blob = new Blob([data], { type: 'application/pdf' });

      if (submitType === 'pdf' || submitType === 'pdf-for-send') {
        const url = URL.createObjectURL(blob);
        download(url, sanitizeFilename(filename));
        return;
      } else if (submitType === 'send') {
        const body = allFormData(this.confirmPanel);
        body.append('file', blob, filename);
        const response = await fetch(
          `${location.origin}/api/activity-sending-requests`,
          { method: 'POST', body }
        );
        if (!response.ok) {
          displayInternalServerError(this.confirmPanel);
          throw new DependencyError(response, await response.text());
        }
        this.confirmPanel.hidden = true;
        this.sentPanel.hidden = false;
        // prevent further unload warnings
        this.hasChanges = false;
        this.querySelectorAll('form-behavior').forEach(el =>
          el.disconnectedCallback()
        );
      } else {
        throw new Error(`Unexpected submit type: ${submitType}`);
      }
    } finally {
      busySubmitButtons(this.primaryForm, false);
      busySubmitButtons(this.confirmPanel, false);
      this.isSubmitting = false;
    }
  }

  pdfFieldChanged() {
    if (this.hasChanges) {
      return;
    }
    this.hasChanges = true;
    window.addEventListener('beforeunload', event => {
      event.preventDefault();
      event.returnValue = 'You have unsaved work.'; // browser overrides this text
    });
  }
}

function download(blobUrl: string, filename: string) {
  const a = document.createElement('a');
  if (!a.click) {
    throw new Error('download: "a.click()" is not supported.');
  }
  a.href = blobUrl;
  a.target = '_parent';
  // Use a.download if available. This increases the likelihood that
  // the file is downloaded instead of opened by another PDF plugin.
  if ('download' in a) {
    a.download = filename;
  }
  // <a> must be in the document for recent Firefox versions,
  // otherwise .click() is ignored.
  (document.body || document.documentElement).append(a);
  a.click();
  a.remove();
}
